/**
 * <Dialog> — HYBRID Dialog primitive (D-05); composed API REQUIRES `title`.
 *
 * Pitfall H8 (Radix Dialog without Title): a `<Dialog.Content>` without an
 * accessibly-named `<Dialog.Title>` fails WCAG 4.1.2 and makes axe scream.
 * Radix itself does not enforce this — it's trivially bypassable. Our composed
 * wrapper closes the hole at COMPILE time:
 *
 *   <Dialog>                       // ✗ TS error — missing `title`
 *   <Dialog title="">              // ✗ TS error — empty string has no brand
 *   <Dialog title={nonEmpty("x")}> // ✓
 *   <Dialog title={{ srOnly: nonEmpty("x") }}> // ✓ — visually hidden, still announced
 *
 * The `{ srOnly: … }` variant is for dialogs whose visual design doesn't show
 * a heading (e.g. a pure image lightbox) — we still mount a `<Dialog.Title>`
 * element wrapped in `.sr-only` so screen readers announce it.
 *
 * Decomposed exports (`DialogRoot`, `DialogTrigger`, etc.) are available for
 * non-standard layouts; apps reach for the composed form first.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../utils";
import { Icon } from "../Icon";
import { Button } from "../Button";
import { nonEmpty, type NonEmptyString } from "../../types/NonEmptyString";

// ---------------------------------------------------------------------------
// Decomposed — styled Radix parts
// ---------------------------------------------------------------------------

export const DialogRoot = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogPortal = RadixDialog.Portal;
export const DialogClose = RadixDialog.Close;

/**
 * `RadixDialog.Content` that hands focus back to whatever had it when the
 * dialog opened. Radix returns focus only to its own `Trigger`; a dialog opened
 * from a plain button, a menu or a URL (controlled `open`) has none, so closing
 * it dropped focus on <body> and a keyboard user lost their place.
 *
 * Every dialog surface in the design system renders this instead of
 * `RadixDialog.Content`. It mounts inside the Portal, which renders only while
 * open, so its first render is the moment of opening: before any `autoFocus`
 * inside has moved focus. A caller's `onCloseAutoFocus` runs first and keeps
 * the last word by calling `preventDefault()`. An opener that is gone by then
 * (a menu item) leaves Radix's own behaviour in place.
 */
export const FocusReturningContent = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(function FocusReturningContent({ onCloseAutoFocus, ...rest }, ref) {
  const [opener] = React.useState(() =>
    typeof document === "undefined" ? null : document.activeElement,
  );
  return (
    <RadixDialog.Content
      ref={ref}
      {...rest}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        if (event.defaultPrevented) return;
        if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected) {
          event.preventDefault();
          opener.focus({ preventScroll: true });
        }
      }}
    />
  );
});

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(function DialogOverlay({ className, ...rest }, ref) {
  return (
    <RadixDialog.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-40 bg-black/50",
        // Motion — overlay dim fades 150ms; honors prefers-reduced-motion.
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:motion-dialog-in",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:motion-dialog-out",
        "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
        className,
      )}
      {...rest}
    />
  );
});

/**
 * Edge-docked panel content — the DRAWER shell.
 *
 * `DialogContent` below is a CENTERED CARD: it hard-codes
 * `top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2` and mounts its own
 * Portal + Overlay. Handing it a drawer className does not turn it into a
 * drawer — `top-0` wins over `top-1/2`, but the translate survives and lifts
 * the panel by half its own height, putting its title and close button above
 * the viewport where nothing can reach them. Cancelling the translate at the
 * call site "works" only until the card's positioning changes underneath it.
 *
 * So a drawer gets its own content primitive with nothing to cancel: docked to
 * the trailing edge via logical `end-0`, full height, no transform at all.
 * Unlike `DialogContent` this does NOT bring its own Portal/Overlay — compose
 * it inside `DialogPortal` + `DialogOverlay` so the consumer controls both.
 */
export const DialogDrawerContent = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(function DialogDrawerContent({ className, children, ...rest }, ref) {
  return (
    <FocusReturningContent
      ref={ref}
      className={cn(
        // Logical `end-0` docks to the right in LTR and the left in RTL. No
        // translate, so nothing has to be undone downstream.
        "fixed z-50 top-0 bottom-0 end-0",
        "h-dvh w-full max-w-lg",
        "bg-surface-elevated border-s border-subtle shadow-lg",
        "flex flex-col overflow-y-auto p-lg",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:motion-dialog-in",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:motion-dialog-out",
        "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
        className,
      )}
      {...rest}
    >
      {children}
    </FocusReturningContent>
  );
});

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(function DialogContent({ className, children, ...rest }, ref) {
  return (
    <RadixDialog.Portal>
      <DialogOverlay />
      <FocusReturningContent
        ref={ref}
        className={cn(
          // Center via logical-style percentage positioning. We avoid `left-*`/
          // `right-*` by using `start-1/2` + translate (start flips to right
          // edge in RTL so `-translate-x-1/2` becomes `+translate-x-1/2`).
          // The explicit `rtl:translate-x-1/2` keeps the centering correct.
          "fixed z-50 top-1/2 start-1/2",
          "-translate-x-1/2 -translate-y-1/2",
          "rtl:translate-x-1/2",
          // Shape + surface — `flex flex-col` so the composed header/body/footer
          // can use `flex-1 overflow-y-auto` on the body region.
          // `linkhr-glass` is a no-op when [data-glass="on"] is not set;
          // when active it floats the dialog as a frosted panel.
          "linkhr-glass w-full max-w-lg rounded-lg bg-surface-elevated shadow-lg",
          "p-lg",
          // Cap height to viewport minus 2rem gutter so the dialog never
          // overflows above/below the screen. Composed wrapper overrides
          // padding to 0 + structures header/body/footer so the body
          // scrolls; decomposed consumers (PdfPreview, ImageCrop, etc.)
          // keep the default p-lg + scroll the inner content themselves.
          "max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden",
          // Motion — pop: fade + scale 0.97→1, 180ms out-quart in / 120ms in out.
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-97 data-[state=open]:motion-dialog-in",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-97 data-[state=closed]:motion-dialog-out",
          "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
          className,
        )}
        {...rest}
      >
        {children}
      </FocusReturningContent>
    </RadixDialog.Portal>
  );
});

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(function DialogTitle({ className, ...rest }, ref) {
  return <RadixDialog.Title ref={ref} className={cn("text-heading", className)} {...rest} />;
});

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(function DialogDescription({ className, ...rest }, ref) {
  return (
    <RadixDialog.Description ref={ref} className={cn("text-label mt-xs", className)} {...rest} />
  );
});

// ---------------------------------------------------------------------------
// Composed — REQUIRES `title` at compile time (H8)
// ---------------------------------------------------------------------------

/**
 * Title prop: either a visible heading string or a `{ srOnly }` object for
 * visually-hidden titles. Both carriers use `NonEmptyString` so empty strings
 * fail to typecheck.
 */
export type DialogTitleProp = NonEmptyString | { srOnly: NonEmptyString };

export interface DialogProps {
  /**
   * REQUIRED — `NonEmptyString` (visible heading) OR `{ srOnly: NonEmptyString }`
   * (visually hidden but announced to AT). Pitfall H8 — every Dialog MUST have
   * an accessible title; this type enforces it at compile time.
   */
  title: DialogTitleProp;
  /** Optional description rendered below the title. */
  description?: string;
  /** Controlled open state. */
  open?: boolean;
  /** Controlled-open change handler. */
  onOpenChange?: (open: boolean) => void;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  /** Trigger element — rendered inside `<Dialog.Trigger asChild>`. */
  trigger?: React.ReactNode;
  /** Body content. */
  children?: React.ReactNode;
  /** Footer slot — typically a `<Button>` row. */
  footer?: React.ReactNode;
  /** Consumer-supplied className forwarded to `DialogContent`. */
  className?: string;
  /**
   * Accessible label for the close-X button. Defaults to the localized
   * common "close" label; pass one to say something more specific.
   */
  closeLabel?: NonEmptyString;
  /**
   * Radix's close auto-focus hook. Focus goes back to whatever had it when the
   * dialog opened (see `FocusReturningContent`); pass this only to send it
   * somewhere else, calling `event.preventDefault()` first.
   */
  onCloseAutoFocus?: ((event: Event) => void) | undefined;
  /**
   * `"alertdialog"` for a dialog that interrupts to confirm something
   * consequential (a destructive confirm): screen readers announce it as an
   * alert. Defaults to Radix's `"dialog"`.
   */
  role?: "dialog" | "alertdialog";
}

function ComposedDialog({
  title,
  description,
  open,
  onOpenChange,
  defaultOpen,
  trigger,
  children,
  footer,
  className,
  closeLabel,
  onCloseAutoFocus,
  role,
}: DialogProps) {
  const { t } = useTranslation("common");
  const isSrOnlyTitle = typeof title === "object" && "srOnly" in title;
  const titleText = isSrOnlyTitle ? title.srOnly : title;

  const rootProps = {
    ...(open !== undefined ? { open } : {}),
    ...(onOpenChange !== undefined ? { onOpenChange } : {}),
    ...(defaultOpen !== undefined ? { defaultOpen } : {}),
  };

  return (
    <RadixDialog.Root {...rootProps}>
      {trigger !== undefined && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      {/* Override the default p-lg from DialogContent — the composed wrapper
          owns padding via header/body/footer so the body scrolls
          edge-to-edge. Consumer className still wins via cn() ordering.

          When no description is supplied, no <DialogDescription> renders, so
          Radix's auto aria-describedby would dangle (pointing at an id that
          doesn't exist) and log a console warning. Explicitly clear it in that
          case; when a description IS present, leave Radix's link intact. */}
      <DialogContent
        className={cn("p-0", className)}
        {...(description === undefined ? { "aria-describedby": undefined } : {})}
        {...(role !== undefined ? { role } : {})}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {/* Header — fixed height; padding lives here so the scrolling body
            can scroll edge-to-edge if it wants. */}
        <div className="flex items-start justify-between gap-md p-lg pb-0">
          <div className="flex-1">
            {isSrOnlyTitle ? (
              <DialogTitle className="sr-only">{titleText}</DialogTitle>
            ) : (
              <DialogTitle>{titleText}</DialogTitle>
            )}
            {description !== undefined && <DialogDescription>{description}</DialogDescription>}
          </div>
          <RadixDialog.Close asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={closeLabel ?? nonEmpty(t("actions.close"))}
            >
              <Icon as={X} name="x" size={20} />
            </Button>
          </RadixDialog.Close>
        </div>

        {/* Body — scrolls when content exceeds the height cap on
            DialogContent (max-h-[calc(100vh-2rem)]). Without this region
            scrolling, tall forms (LeaveTypeDrawer etc.) used to extend
            past both edges of the viewport. */}
        {children !== undefined && (
          <div className="flex-1 overflow-y-auto px-lg pt-md">{children}</div>
        )}

        {/* Footer — fixed; padding lives here so the body can scroll
            independently. */}
        {footer !== undefined && <div className="flex justify-end gap-sm p-lg pt-md">{footer}</div>}
      </DialogContent>
    </RadixDialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Dotted composition — matches Card/Select/Popover pattern (D-05).
// Consumers can use either flat (`<DialogRoot>…<DialogContent>`) or dotted
// (`<Dialog.Root>…<Dialog.Content>`) decomposed APIs, alongside the composed
// `<Dialog title=…>` wrapper exported as the main namespace.
// ---------------------------------------------------------------------------

type DialogComposition = typeof ComposedDialog & {
  Root: typeof DialogRoot;
  Trigger: typeof DialogTrigger;
  Portal: typeof DialogPortal;
  Close: typeof DialogClose;
  Overlay: typeof DialogOverlay;
  Content: typeof DialogContent;
  Title: typeof DialogTitle;
  Description: typeof DialogDescription;
};

export const Dialog = ComposedDialog as DialogComposition;
Dialog.Root = DialogRoot;
Dialog.Trigger = DialogTrigger;
Dialog.Portal = DialogPortal;
Dialog.Close = DialogClose;
Dialog.Overlay = DialogOverlay;
Dialog.Content = DialogContent;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;
